import { useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  FilterStrip,
  ResultFooter,
  WindowLine,
} from "../components/filters/ConsoleFilterContract";
import {
  CONSOLE_WINDOW_LIVE,
  DEFAULT_LIVE_TIME_PRESET,
  LIVE_TIME_PRESETS,
  isLiveTimePreset,
  liveFilterChips,
  liveResultCopy,
  resolveLiveTimeBounds,
  type LiveTimePreset,
  type ConsoleFilterChipLabel,
} from "../data/console-filter-contract";
import {
  checkpointDisplayLabel,
  fetchScanList,
  gpsQualityLabel,
  isAdHocPresence,
  presenceKindLabel,
  type ScanRow,
} from "../data/scan-list";
import { cadRealtimeFeed } from "../data/cad-feed";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

type KeyedLiveLoad =
  | {
      readonly requestKey: string;
      readonly outcome: { readonly kind: "read"; readonly rows: ScanRow[] };
    }
  | {
      readonly requestKey: string;
      readonly outcome: { readonly kind: "error"; readonly message: string };
    };

// Live Activity surface — latest captures from supervisor_scan_list with Site/Guard/time
// filters applied before the 50-row window. Realtime on checkpoint_scans with polling fallback.
// Map Filter paint/camera stay untouched (list-local only).
export function LiveActivity({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  enableRealtime = true,
  onSelect,
  selectedClientEventId = null,
  scopeToken,
  onScopeChange,
  now,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  enableRealtime?: boolean;
  /** When set, activity tiles are buttons that ask CAD to focus that capture. */
  onSelect?: ((row: ScanRow) => void) | undefined;
  selectedClientEventId?: string | null;
  /** Opaque generation token from the page (account + support). Changes reset filters. */
  scopeToken?: string;
  /** Clears master-detail / map selection when the list scope changes. */
  onScopeChange?: () => void;
  /** Injected clock for tests; production omits and uses wall time. */
  now?: Date;
}) {
  const [siteFilter, setSiteFilter] = useState("");
  const [guardFilter, setGuardFilter] = useState("");
  const [timePreset, setTimePreset] = useState<LiveTimePreset>(DEFAULT_LIVE_TIME_PRESET);
  const [siteOptions, setSiteOptions] = useState<string[]>([]);
  const [guardOptions, setGuardOptions] = useState<string[]>([]);

  const requestKey = JSON.stringify({
    accountId,
    scopeToken,
    siteFilter,
    guardFilter,
    timePreset,
  });
  const currentRequestKeyRef = useRef(requestKey);
  currentRequestKeyRef.current = requestKey;
  const previousRequestKeyRef = useRef(requestKey);

  useEffect(() => {
    if (previousRequestKeyRef.current === requestKey) return;
    previousRequestKeyRef.current = requestKey;
    onScopeChange?.();
  }, [requestKey, onScopeChange]);

  useEffect(() => {
    setSiteFilter("");
    setGuardFilter("");
    setTimePreset(DEFAULT_LIVE_TIME_PRESET);
    setSiteOptions([]);
    setGuardOptions([]);
  }, [client, accountId, scopeToken]);

  const { data, error, reload } = useReadModel<KeyedLiveLoad>(
    async () => {
      try {
        const bounds = resolveLiveTimeBounds(timePreset, timeZone, now ?? new Date());
        const rows = await fetchScanList(client, accountId, CONSOLE_WINDOW_LIVE.limit, {
          siteName: siteFilter.length > 0 ? siteFilter : null,
          guardName: guardFilter.length > 0 ? guardFilter : null,
          receivedFromIso: bounds.fromIso,
          receivedToIso: bounds.toIso,
        });
        return { requestKey, outcome: { kind: "read", rows } };
      } catch {
        return {
          requestKey,
          outcome: { kind: "error", message: "Could not load live activity." },
        };
      }
    },
    [client, requestKey, timeZone, now],
    {
      pollMs,
      ...(enableRealtime
        ? { realtime: cadRealtimeFeed(client, accountId, "activity", "checkpoint_scans") }
        : {}),
    },
  );

  const currentLoad = data?.requestKey === requestKey ? data : null;
  const currentError =
    currentLoad?.outcome.kind === "error"
      ? currentLoad.outcome.message
      : error !== null && currentLoad === null && data === null
        ? error
        : null;
  const rows = currentLoad?.outcome.kind === "read" ? currentLoad.outcome.rows : [];

  useEffect(() => {
    if (currentLoad?.outcome.kind !== "read") return;
    const loadedRows = currentLoad.outcome.rows;
    setSiteOptions((current) => {
      const next = new Set(current);
      for (const row of loadedRows) {
        if (row.siteName !== null && row.siteName.trim().length > 0) {
          next.add(row.siteName.trim());
        }
      }
      return [...next].sort((left, right) => left.localeCompare(right));
    });
    setGuardOptions((current) => {
      const next = new Set(current);
      for (const row of loadedRows) {
        if (row.guardName !== null && row.guardName.trim().length > 0) {
          next.add(row.guardName.trim());
        }
      }
      return [...next].sort((left, right) => left.localeCompare(right));
    });
  }, [currentLoad]);

  // Seed picker options from a default-bound unscoped-attribute read so chips stay after filter.
  useEffect(() => {
    let cancelled = false;
    const bounds = resolveLiveTimeBounds(DEFAULT_LIVE_TIME_PRESET, timeZone, now ?? new Date());
    void fetchScanList(client, accountId, CONSOLE_WINDOW_LIVE.limit, {
      receivedFromIso: bounds.fromIso,
      receivedToIso: bounds.toIso,
    })
      .then((seedRows) => {
        if (cancelled) return;
        setSiteOptions((current) => {
          const next = new Set(current);
          for (const row of seedRows) {
            if (row.siteName !== null && row.siteName.trim().length > 0) {
              next.add(row.siteName.trim());
            }
          }
          return [...next].sort((left, right) => left.localeCompare(right));
        });
        setGuardOptions((current) => {
          const next = new Set(current);
          for (const row of seedRows) {
            if (row.guardName !== null && row.guardName.trim().length > 0) {
              next.add(row.guardName.trim());
            }
          }
          return [...next].sort((left, right) => left.localeCompare(right));
        });
      })
      .catch(() => {
        // List owns visible errors; picker options keep last safe values.
      });
    return () => {
      cancelled = true;
    };
  }, [client, accountId, scopeToken, timeZone, now]);

  const filtersActive =
    siteFilter.length > 0 ||
    guardFilter.length > 0 ||
    timePreset !== DEFAULT_LIVE_TIME_PRESET;
  const windowCopy = liveResultCopy({
    shownCount: rows.length,
    siteFilter,
    guardFilter,
    timePreset,
  });
  const filterChips = liveFilterChips({ siteFilter, guardFilter, timePreset });

  function clearAll(): void {
    setSiteFilter("");
    setGuardFilter("");
    setTimePreset(DEFAULT_LIVE_TIME_PRESET);
  }

  function removeFilterChip(label: ConsoleFilterChipLabel): void {
    if (label === "Site") setSiteFilter("");
    if (label === "Guard") setGuardFilter("");
  }

  if (data === null && currentError === null) {
    return <p className="surface__state" role="status">Loading live activity…</p>;
  }
  if (currentError !== null && rows.length === 0 && currentLoad === null && data === null) {
    return (
      <div className="report__state-pane">
        <div>
          <p className="surface__state surface__state--error" role="alert">
            {currentError}
          </p>
          <button className="report__retry" type="button" onClick={reload}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const waitingForCurrent = currentLoad === null && currentError === null;
  const showError =
    currentError !== null && (currentLoad === null || currentLoad.outcome.kind === "error");

  return (
    <div className="live-activity">
      <div className="report__toolbar">
        <label className="report__filter">
          <span className="report__filter-label">Site</span>
          <select
            aria-label="Filter by site"
            value={siteFilter}
            onChange={(event) => setSiteFilter(event.target.value)}
          >
            <option value="">All sites</option>
            {siteOptions.map((site) => (
              <option key={site} value={site}>
                {site}
              </option>
            ))}
          </select>
        </label>
        <label className="report__filter">
          <span className="report__filter-label">Guard</span>
          <select
            aria-label="Filter by guard"
            value={guardFilter}
            onChange={(event) => setGuardFilter(event.target.value)}
          >
            <option value="">All guards</option>
            {guardOptions.map((guard) => (
              <option key={guard} value={guard}>
                {guard}
              </option>
            ))}
          </select>
        </label>
        <label className="report__filter">
          <span className="report__filter-label">Time</span>
          <select
            aria-label="Filter by time"
            value={timePreset}
            onChange={(event) => {
              const value = event.target.value;
              if (isLiveTimePreset(value)) setTimePreset(value);
            }}
          >
            {LIVE_TIME_PRESETS.map((preset) => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <FilterStrip
        chips={filterChips}
        onClearAll={clearAll}
        onRemoveChip={removeFilterChip}
      />

      <WindowLine variant={CONSOLE_WINDOW_LIVE} />

      {showError ? (
        <div className="report__state-pane">
          <div>
            <p className="surface__state surface__state--error" role="alert">
              {currentError}
            </p>
            <button className="report__retry" type="button" onClick={reload}>
              Retry
            </button>
          </div>
        </div>
      ) : waitingForCurrent ? (
        <p className="surface__state" role="status">
          Loading live activity…
        </p>
      ) : rows.length === 0 && !filtersActive ? (
        <>
          <p className="surface__state" role="status">
            No recent activity.
          </p>
          <ResultFooter state={windowCopy.state} foot={windowCopy.foot} />
        </>
      ) : rows.length === 0 ? (
        <div className="report__table-pane">
          <div className="report__empty" role="status">
            <div>
              <p className="report__state-kicker">Empty</p>
              <p>{windowCopy.foot}</p>
              <button type="button" onClick={clearAll}>
                Clear all
              </button>
            </div>
          </div>
          <ResultFooter state={windowCopy.state} foot={windowCopy.foot} />
        </div>
      ) : (
        <>
          <div className="activity-rail" aria-label="Recent checkpoint scans">
            <div className="activity-rail__track" role="list">
              {rows.map((row, index) => (
                <ActivityBubble
                  key={row.clientEventId || `${row.serverReceivedAt}-${index}`}
                  row={row}
                  timeZone={timeZone}
                  isLatest={index === 0}
                  selected={
                    selectedClientEventId !== null &&
                    selectedClientEventId.length > 0 &&
                    row.clientEventId === selectedClientEventId
                  }
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
          <ResultFooter state={windowCopy.state} foot={windowCopy.foot} />
        </>
      )}
    </div>
  );
}

function ActivityBubble({
  row,
  timeZone,
  isLatest,
  selected,
  onSelect,
}: {
  row: ScanRow;
  timeZone: string;
  isLatest: boolean;
  selected: boolean;
  onSelect?: ((row: ScanRow) => void) | undefined;
}) {
  const bubbleClass = [
    "activity-bubble",
    `activity-bubble--${row.lifecycleState}`,
    isLatest ? "activity-bubble--latest" : "",
    selected ? "activity-bubble--selected" : "",
    onSelect !== undefined ? "activity-bubble--selectable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const adHoc = isAdHocPresence(row);
  const kindLabel = presenceKindLabel(row);
  const showMethodChip = kindLabel === null;
  const title = checkpointDisplayLabel(row);
  const selectLabel = `Show ${title} on map`;

  const body = (
    <>
      <header className="activity-bubble__head">
        <div className="activity-bubble__badges">
          <span className={`activity-bubble__status activity-bubble__status--${row.lifecycleState}`}>
            {row.lifecycleState}
          </span>
          {kindLabel !== null && (
            <span
              className="activity-bubble__presence-badge"
              aria-label="Manual presence, not an NFC or QR scan"
              title="Manual presence, not an NFC or QR scan"
            >
              {kindLabel}
            </span>
          )}
        </div>
        <time className="activity-bubble__time" dateTime={row.serverReceivedAt}>
          {formatDisplayTime(row.serverReceivedAt, timeZone)}
        </time>
      </header>
      <strong className="activity-bubble__cp">{title}</strong>
      <p className="activity-bubble__meta">
        {showMethodChip && <span className="activity-bubble__chip">{row.scanMethod}</span>}
        <span className="activity-bubble__chip">{row.guardName ?? "-"}</span>
        <span className="activity-bubble__chip">{gpsQualityLabel(row)}</span>
        {adHoc && row.reviewRequired === true && (
          <span className="activity-bubble__chip activity-bubble__chip--review" aria-label="Review required">
            Review required
          </span>
        )}
      </p>
      {row.guardNote != null && row.guardNote.length > 0 && (
        <p className="activity-bubble__guard-note" aria-label="Guard note">
          <span className="activity-bubble__guard-note-label">Note</span>
          <span className="activity-bubble__guard-note-text">{row.guardNote}</span>
        </p>
      )}
      {onSelect !== undefined ? (
        <span className="activity-bubble__map-hint">Show on map</span>
      ) : null}
    </>
  );

  if (onSelect !== undefined) {
    return (
      <button
        type="button"
        className={bubbleClass}
        role="listitem"
        aria-label={selectLabel}
        aria-pressed={selected}
        onClick={() => onSelect(row)}
      >
        {body}
      </button>
    );
  }

  return (
    <article className={bubbleClass} role="listitem">
      {body}
    </article>
  );
}
